import {
    setUserData,
    getUserData,
    PlainObject,
    loginWithGameBackendCredentials,
    postScore,
    setUserProfile,
    getFriendsScores,
    LeaderboardEntry,
    registerWithFacebookInstantGameSignature as gbcRegisterWithFacebookInstantGameSignature,
    syncFriendListByPlatformPlayerId as gbcSyncFriendListByPlatformPlayerId,
    trackBotMessageConversion as gbcTrackBotMessageConversion,
    getServerTimestamp,
} from '@sgorg/game-backend-client';
import IPlayerData from '../../Player/IPlayerData';
import Game from '../../Game';
import { Retrier, createThrottler, sleep, retry } from '../async';
import { Random } from '../Random';
import { Deferred } from '../Deferred';
import { Categorizer, convertError, report } from '../../report';
import { fbPendingRequestCategorizer } from './Drivers/FBHelpers';
import { track } from '../Tracking/Track';

export namespace GBCHandler {
    // Login

    interface AuthData {
        userId: string;
        authToken: string;
    }

    const loggedIn = new Deferred<boolean>(false);

    export function login(params: {
        gameId: string;
        cacheRead: () => Promise<AuthData | undefined>;
        cacheWrite: (data: AuthData | null) => Promise<void>;
        register: () => Promise<AuthData>;
    }) {
        const { gameId, cacheRead, cacheWrite, register } = params;
        return retry<AuthData>(async () => {
            let caching = Promise.resolve();
            let authData: AuthData | null = null;
            try {
                authData = await getAuthDataFromCache();
                if (!authData) {
                    authData = await register();
                    if (!isValidAuthData(authData)) {
                        throw new Error('Failed to register');
                    }
                    caching = cacheAuthData(authData).catch(cachingError => {
                        // log to sentry but don't propagate up as this is a nice-to-have
                        report('cacheAuthData failed', {
                            once: true,
                            sampleRate: 0.1,
                            error: cachingError,
                            categorizers: [fbPendingRequestCategorizer('cacheAuthData')],
                        });
                    });
                }
                await loginWithGameBackendCredentials({
                    userId: authData.userId,
                    authToken: authData.authToken,
                    gameId,
                });
            } catch (error) {
                try {
                    await caching;
                    await invalidateAuthDataCache();
                } catch (invalidationError) {
                    // log to sentry, but don't stop retry logic
                    report('invalidateAuthDataCache failed', {
                        once: true,
                        sampleRate: 0.1,
                        error: invalidationError,
                        categorizers: [fbPendingRequestCategorizer('invalidateAuthDataCache')],
                    });
                }
                throw convertError(error, 'login flow');
            }
            // TODO: await caching?
            return authData;
        }).promise.then(
            authData => {
                loggedIn.control.resolve(true);
                return authData;
            },
            error => {
                loggedIn.control.reject(error);
                throw error;
            }
        );

        function isValidAuthData(potentialAuthData: any): potentialAuthData is AuthData {
            return potentialAuthData && potentialAuthData.userId && potentialAuthData.authToken;
        }

        async function getAuthDataFromCache() {
            const authData = await cacheRead();
            return isValidAuthData(authData) ? authData : undefined;
        }
        function cacheAuthData(data: AuthData) {
            return cacheWrite(data);
        }
        function invalidateAuthDataCache() {
            return cacheWrite(null);
        }
    }

    export async function onLogin(params: {
        syncFriendsList: () => Promise<void>;
        userProfile: Deferred<IGBUserProfile>;
        botMessageId: Deferred<string>;
    }): Promise<void> {
        params.syncFriendsList();
        params.userProfile.read.nowOrThen(userProfile => {
            GBCHandler.updateUserProfile(userProfile);
            track('userID', Game.sdk.getUserId());
        });

        params.botMessageId.read.nowOrThen(messageId => {
            GBCHandler.trackBotMessageConversion({ messageId });
        });
    }

    export function registerWithFacebookInstantGameSignature(
        ...args: Parameters<typeof gbcRegisterWithFacebookInstantGameSignature>
    ) {
        return gbcRegisterWithFacebookInstantGameSignature(...args);
    }

    //

    export function syncFriendListByPlatformPlayerId(...args: Parameters<typeof gbcSyncFriendListByPlatformPlayerId>) {
        return retry(
            async () => {
                await gbcSyncFriendListByPlatformPlayerId(...args);
                console.info('syncFriendsList succeed');
            },
            {
                getRetryDelayMs: () => 1000,
                onError: (error, attemptIndex) => {
                    console.warn('syncFriendsList failed:', error);
                    if (attemptIndex === 5) {
                        report('syncFriendsList failed 5 times in row', {
                            error,
                            once: true,
                            categorizers: [gbcNetworkErrorCategorizer('syncFriendsList')],
                        });
                    }
                },
            }
        ).promise;
    }

    export async function trackBotMessageConversion(...args: Parameters<typeof gbcTrackBotMessageConversion>) {
        try {
            await retry(() => gbcTrackBotMessageConversion(...args)).promise;
        } catch (error) {
            report('gbcTrackBotMessageConversion failed', {
                once: true,
                sampleRate: 0.1,
                error,
            });
        }
    }

    // Saving

    // TODO: persist across sessions?
    const saveRetrier = new Retrier(
        retryIndex => {
            const second = 1000;
            const minute = 60 * second;
            const delays = [0.1 * second, 0.5 * second, 1 * second, 5 * second, 30 * second, minute, 5 * minute];
            const delay = delays[Math.min(retryIndex, delays.length - 1)];

            // Prevent synchronisation across players
            return Random.value(delay * 0.9, delay * 1.1);
        },
        (error, attemptIndex) => {
            if (attemptIndex === 5) {
                report('Failed to save 5 times in row', {
                    error,
                    once: true,
                    categorizers: [gbcNetworkErrorCategorizer('updateGBSaveData')],
                });
            }
        }
    );

    const savingStuff: {
        remote: { [key in keyof IPlayerData]?: PlainObject };
        scheduled: { [key in keyof IPlayerData]?: PlainObject };
    } = {
        remote: {},
        scheduled: {},
    };

    const saveThrottler = createThrottler(1500);

    export function updateGBSaveData(data: Partial<IPlayerData>) {
        // @ts-ignore // todo: Allow other values. Arrays
        const newDataToSave = toPlainObject(data);
        const keysOfChangedValues = Object.keys(newDataToSave).filter((key: keyof IPlayerData) => {
            if (savingStuff.scheduled.hasOwnProperty(key)) {
                return !simpleEqual(newDataToSave[key], savingStuff.scheduled[key]);
            }
            if (savingStuff.remote.hasOwnProperty(key)) {
                return !simpleEqual(newDataToSave[key], savingStuff.remote[key]);
            }
            return true;
        });
        const dataTimestampKey: keyof Pick<IPlayerData, 'data_timestamp'> = 'data_timestamp';
        if (keysOfChangedValues.filter(key => key !== dataTimestampKey).length > 0) {
            savingStuff.scheduled = {
                ...savingStuff.scheduled,
                ...newDataToSave,
            };
            loggedIn.read.promise.then(
                canProceed => {
                    if (canProceed) {
                        saveRetrier.restart(saveToGBC, false);
                    }
                },
                // Nothing to do if it failed and no need to raise the error
                () => {}
            );
        }
    }

    async function saveToGBC() {
        await saveThrottler();
        if (Object.keys(savingStuff.scheduled).length === 0) {
            return;
        }
        // Copy what we're about to save
        // so that when it succeeds we know (to some extent) what doesn't need to be saved anymore
        // WARNING: this assumes this object is flat
        const dataBeingSaved = { ...savingStuff.scheduled };
        try {
            // As of 04jun20 I've been told that:
            // * entries with value of undefined are ignored (reason - JSON)
            // * entries with value of null are saved as null
            // * entries with value of an object will not be merged with an existing object,
            //   instead will just replace whatever value was there before
            await setUserData(dataBeingSaved);
        } catch (error) {
            throw convertError(error, 'setUserData');
        }

        console.log('Saved to GBC');
        for (const [key, value] of Object.entries(dataBeingSaved)) {
            const k: keyof IPlayerData = key as keyof IPlayerData;
            savingStuff.remote[k] = value;
            if (simpleEqual(savingStuff.scheduled[k], value)) {
                delete savingStuff.scheduled[k];
            }
        }
    }

    export async function debugLoadFromGBC() {
        return await getUserData();
    }

    /**
     * Does not support circular structures
     * Does not look closely at prototypes and functions
     * Does not compare advances data types like Map and Set
     */
    function simpleEqual(a: any, b: any) {
        if (a === b) return true;
        if (typeof a !== typeof b) return false;
        if (typeof a === 'number' && isNaN(a) && isNaN(b)) return true;
        if (typeof a !== 'object' || a === null || b === null) return false;
        if (Array.isArray(a) !== Array.isArray(b)) return false;

        const compared = Object.create(null);
        for (const key in a) {
            if (!Object.prototype.hasOwnProperty.call(a, key)) continue;
            if (!Object.prototype.hasOwnProperty.call(b, key)) return false;
            if (!simpleEqual(a[key], b[key])) return false;
            compared[key] = true;
        }
        for (const key in b) {
            if (compared[key]) continue;
            if (!Object.prototype.hasOwnProperty.call(b, key)) continue;
            if (!Object.prototype.hasOwnProperty.call(a, key)) return false;
            if (!simpleEqual(b[key], a[key])) return false;
        }

        return true;
    }

    function toPlainObject(data: { [key: string]: string | number | null | undefined | boolean }): PlainObject {
        const plainObject: PlainObject = {};

        Object.entries(data).forEach(([key, value]) => {
            if (value === undefined) {
                report('Attempting to plain object with entry value undefined', {
                    sampleRate: 0.1,
                    once: true,
                    tags: { key },
                });
                return;
            }
            if (value === null) {
                plainObject[key] = value;
            } else if (typeof value === 'boolean' || typeof value === 'number' || typeof value === 'string') {
                plainObject[key] = String(value);
            } else {
                report('Unexpected value type', {
                    sampleRate: 0.1,
                    once: true,
                    tags: {
                        key,
                        type: typeof value,
                    },
                });
            }
        });

        return plainObject;
    }

    //

    export async function updateLeaderboard(leaderboardName: string, instanceId: string, score: number): Promise<void> {
        const canProceed = await loggedIn.read.promise.catch(() => false);
        if (!canProceed) return;

        try {
            await retry(
                () =>
                    postScore({
                        leaderboardName,
                        instanceId,
                        score: score || 0,
                    }),
                {
                    onError: (error, count) => {
                        console.warn(
                            `postScore failed #${count}`,
                            `leaderboardName = "${leaderboardName}"`,
                            `instanceId = "${instanceId}"`,
                            `score = ${score}`,
                            error
                        );
                    },
                }
            ).promise;
        } catch (error) {
            report('postScore failed', {
                once: true,
                sampleRate: 0.1,
                error,
                tags: {
                    leaderboardName,
                    instanceId,
                    score: score || 0,
                },
                categorizers: [gbcNetworkErrorCategorizer('postScore')],
            });
        }
    }

    export type IGBUserProfile = Parameters<typeof setUserProfile>[0];

    export async function updateUserProfile(userProfile: IGBUserProfile) {
        try {
            await retry(() => setUserProfile(userProfile)).promise;
        } catch (error) {
            report('setUserProfile failed', {
                once: true,
                sampleRate: 0.1,
                error,
                categorizers: [gbcNetworkErrorCategorizer('setUserProfile')],
            });
        }
    }

    export async function fetchLeaderboard(leaderboardName: string, instanceId: string): Promise<LeaderboardEntry[]> {
        const canProceed = await loggedIn.read.promise.catch(() => false);
        if (!canProceed) return [];
        try {
            return await retry(
                () =>
                    getFriendsScores({
                        instanceId: instanceId,
                        leaderboardName: leaderboardName,
                    }),
                {
                    getRetryDelayMs: index => [100][index],
                    onError: (error, count) => {
                        console.warn(
                            `getFriendsScores failed #${count}`,
                            `leaderboardName = "${leaderboardName}"`,
                            `instanceId = "${instanceId}"`,
                            error
                        );
                    },
                }
            ).promise;
        } catch (error) {
            report('getFriendsScores failed', {
                once: true,
                sampleRate: 0.1,
                error,
                tags: {
                    leaderboardName,
                    instanceId,
                },
                categorizers: [gbcNetworkErrorCategorizer('getFriendsScores')],
            });
            return [];
        }
    }

    export function getGBCTimestamp(): Promise<number> {
        const retrier = new Retrier(undefined, (error, attemptIndex) => {
            if (attemptIndex === 2) {
                report('Failed to get timestamp 3 times in row', {
                    error,
                    once: true,
                    categorizers: [gbcNetworkErrorCategorizer('getServerTimestamp')],
                });
            }
        });
        return retrier.restart(getServerTimestamp, false) as Promise<number>;
    }
}

// Error reporting stuff

function gbcNetworkErrorCategorizer(funcName: string): Categorizer {
    return (error: any) => {
        const notLoggedIn = String(error?.message).includes('flag "loggedIn" did not reach asserted state');
        const timedOut = /\b(timed out)\b/i.test(String(error?.message));
        if (notLoggedIn || timedOut) {
            return {
                fingerprint: 'GBCNetworkError',
                tags: {
                    notLoggedIn,
                    timedOut,
                    funcName,
                },
            };
        }
    };
}
